import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Sprout, Calendar, Edit2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CycleManagement = () => {
  const [cycles, setCycles] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCycle, setSelectedCycle] = useState(null);

  const fetchCycles = async () => {
    try {
      const token = localStorage.getItem('token');
      const [cyclesRes, roomsRes] = await Promise.all([
        axios.get(`${API}/room-cycles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/rooms`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      // Handle both array and object with 'cycles' key response formats
      const cyclesData = Array.isArray(cyclesRes.data) ? cyclesRes.data : (cyclesRes.data.cycles || []);
      setCycles(cyclesData);
      setRooms(roomsRes.data);
    } catch (error) {
      console.error('Failed to fetch cycles:', error);
      toast.error('Failed to fetch room cycles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCycles();
  }, []);

  const getPhaseColor = (phase) => {
    const colors = {
      spawning: 'bg-emerald-500 text-white',
      spawn_run: 'bg-green-400 text-white',
      casing: 'bg-blue-500 text-white',
      case_run: 'bg-cyan-400 text-white',
      ruffling: 'bg-indigo-500 text-white',
      covering: 'bg-teal-500 text-white',
      airing: 'bg-lime-500 text-white',
      flush_1: 'bg-yellow-400 text-yellow-900',
      rest: 'bg-gray-400 text-white',
      flush_2: 'bg-orange-500 text-white',
      completed: 'bg-gray-300 text-gray-600'
    };
    return colors[phase] || 'bg-gray-100 text-gray-800';
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.name : 'Unknown Room';
  };

  const activeCycles = cycles.filter(c => c.status === 'active');
  const completedCycles = cycles.filter(c => c.status !== 'active');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Room Cycles</h2>
        <Badge variant="outline" className="text-sm">
          {activeCycles.length} Active / {cycles.length} Total
        </Badge>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading room cycles...</div>
      ) : cycles.length === 0 ? (
        <Card className="border-gray-200">
          <CardContent className="py-12 text-center text-gray-500">
            <Sprout className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No room cycles yet.</p>
            <p className="text-sm mt-2">Assign a batch to a room from the Composting section to start a cycle.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Active Cycles */}
          {activeCycles.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Active Cycles ({activeCycles.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeCycles.map((cycle) => (
                  <Card key={cycle.id} className="border-l-4 border-l-[#1A4D2E]" data-testid={`cycle-card-${cycle.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base font-bold">{getRoomName(cycle.room_id)}</CardTitle>
                        <Badge className={getPhaseColor(cycle.current_phase)}>
                          {cycle.current_phase?.replace('_', ' ')}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p className="text-sm text-gray-600">{cycle.batch_name}</p>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Day {cycle.current_day}/45</span>
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#1A4D2E] transition-all"
                            style={{ width: `${(cycle.current_day / 45) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1 pt-2 border-t">
                        <p>Started: {format(new Date(cycle.start_date), 'MMM d, yyyy')}</p>
                        <p>Expected End: {format(new Date(cycle.expected_completion), 'MMM d, yyyy')}</p>
                      </div>
                      {cycle.has_delay && (
                        <div className="flex items-center gap-1 text-red-600 text-xs mt-2">
                          <AlertTriangle className="w-3 h-3" />
                          <span>Delayed: {cycle.delay_reason}</span>
                        </div>
                      )}
                      {/* Adjust Dates Button */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full mt-3 text-[#1A4D2E] border-[#1A4D2E] hover:bg-[#1A4D2E]/10"
                        onClick={() => setSelectedCycle(cycle)}
                        data-testid={`adjust-dates-btn-${cycle.id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        Adjust Dates / Report Delay
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Completed Cycles */}
          {completedCycles.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Completed Cycles ({completedCycles.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {completedCycles.slice(0, 6).map((cycle) => (
                  <Card key={cycle.id} className="border-gray-200 opacity-75">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base font-medium text-gray-600">{getRoomName(cycle.room_id)}</CardTitle>
                        <Badge variant="outline" className="text-gray-500">Completed</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-500">{cycle.batch_name}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        {format(new Date(cycle.start_date), 'MMM d')} - {cycle.completed_at ? format(new Date(cycle.completed_at), 'MMM d, yyyy') : 'In progress'}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Date Adjustment Modal */}
      {selectedCycle && (
        <DateAdjustmentModal
          cycle={selectedCycle}
          roomName={getRoomName(selectedCycle.room_id)}
          onClose={() => setSelectedCycle(null)}
          onSuccess={() => {
            setSelectedCycle(null);
            fetchCycles();
          }}
        />
      )}
    </div>
  );
};

// Date Adjustment Modal Component
const DateAdjustmentModal = ({ cycle, roomName, onClose, onSuccess }) => {
  const [selectedField, setSelectedField] = useState('');
  const [newDate, setNewDate] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const dateFields = [
    { field: 'casing_date', label: 'Casing Date', day: 13, current: cycle.casing_date },
    { field: 'ruffling_date', label: 'Ruffling Date', day: 19, current: cycle.ruffling_date },
    { field: 'covering_date', label: 'Covering Date', day: 21, current: cycle.covering_date },
    { field: 'airing_start_date', label: 'Airing Start', day: 21, current: cycle.airing_start_date },
    { field: 'flush1_start_date', label: 'Flush 1 Start', day: 33, current: cycle.flush1_start_date },
    { field: 'flush2_start_date', label: 'Flush 2 Start', day: 41, current: cycle.flush2_start_date },
  ];

  const handleSubmit = async () => {
    if (!selectedField || !newDate) {
      toast.error('Please select a date field and new date');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/room-cycles/${cycle.id}/update-date`,
        {
          date_field: selectedField,
          new_date: new Date(newDate).toISOString(),
          delay_reason: reason || 'Date adjusted by grower'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Date updated - Owner has been notified');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update date');
    } finally {
      setLoading(false);
    }
  };

  const selectedFieldData = dateFields.find(f => f.field === selectedField);
  const isDelay = selectedFieldData && newDate && new Date(newDate) > new Date(selectedFieldData.current);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="date-adjustment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#1A4D2E]" />
            Adjust Dates - {roomName}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <p className="text-sm text-gray-600">
            Current Phase: <span className="font-medium">{cycle.current_phase?.replace('_', ' ')}</span> (Day {cycle.current_day}/45)
          </p>

          {/* Date Field Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Date to Adjust
            </label>
            <div className="grid grid-cols-2 gap-2">
              {dateFields.map((df) => (
                <button
                  key={df.field}
                  onClick={() => {
                    setSelectedField(df.field);
                    setNewDate(df.current ? df.current.split('T')[0] : '');
                  }}
                  className={`p-3 text-left rounded-lg border transition-all ${
                    selectedField === df.field
                      ? 'border-[#1A4D2E] bg-[#1A4D2E]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`date-field-${df.field}`}
                >
                  <p className="text-sm font-medium">{df.label}</p>
                  <p className="text-xs text-gray-500">
                    Day {df.day} - {df.current ? format(new Date(df.current), 'MMM d') : 'Not set'}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* New Date Input */}
          {selectedField && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Date
              </label>
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E]"
                data-testid="new-date-input"
              />
              {isDelay && (
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  This is a delay - Owner will be notified
                </p>
              )}
            </div>
          )}

          {/* Reason Input */}
          {selectedField && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason {isDelay && <span className="text-red-500">*</span>}
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this date being changed?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] resize-none"
                rows={2}
                data-testid="reason-input"
              />
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 bg-[#1A4D2E] hover:bg-[#143D23] text-white"
            onClick={handleSubmit}
            disabled={loading || !selectedField || !newDate}
            data-testid="submit-date-change"
          >
            {loading ? 'Updating...' : isDelay ? 'Report Delay' : 'Update Date'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CycleManagement;
